import { Icon } from '@natera/material/lib/icon'
import { useStore } from '@tanstack/react-form'
import { isToday, isYesterday } from 'date-fns'
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSubcategories } from '~/entities/categories'
import {
  CLAIM_PHASES,
  DOCUMENT_SERVER_STATUS,
  getAutoAcceptPatientMatchIndex,
  isLastStep,
  isTaggingClassification,
  PRIOR_AUTH_PHASES,
  ProcessingAlert,
  useAssignReviewer,
  useAuditTrailEvents,
  useCurrentDocument,
  useDocumentExtractedDenialData,
  useDocumentProcessingPolling,
  type DataExtractionFieldName,
} from '~/entities/document'
import { FLAG_TYPES, StepFlagButton, useStepFlagState } from '~/entities/document-flags'
import { usePayors } from '~/entities/payors'
import {
  AuditTrail,
  LoadingSkeleton as AuditTrailLoadingSkeleton,
  useAuditTrail,
} from '~/features/audit-trail'
import {
  StepSessionProvider,
  trackQueueSelected,
  useFirstInteraction,
  useStepCompletion,
  useStepView,
} from '~/features/document-analytics'
import { closePdfTabOrRemind } from '~/features/pdf-tab-reminder'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatCurrency } from '~/shared/lib/currency'
import { formatDateDefault, formatTimeDefault } from '~/shared/lib/datetime'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useAppForm } from '~/shared/lib/form'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import {
  Button,
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  Dialog,
  InfoTip,
  LocalIcon,
  OptiWalkingWithCheckmark,
  ScrollArea,
  Segment,
  SegmentedButton,
  Text,
  Textarea,
} from '~/shared/ui'
import { DocumentPDF } from '~/widgets/document-review-layout'
import { usePatientMatches, usePatientPaMatches } from '../api'
import { NO_DATA_PLACEHOLDER } from '../config'
import type { HoveredField, NoMatchError, PatientMatch } from '../model'
import {
  useCreatePaPatientMatchAndValidate,
  useCreatePatientMatchAndValidate,
  useSubmitPaMatchingReview,
  useSubmitPatientMatchingNotes,
  useSubmitPatientMatchingReview,
} from '../model'
import { LoadingSkeleton } from './patients/loading-skeleton'
import { Patients } from './patients/patients'

export function DocumentPatientMatchingPage() {
  const {
    data: { document },
  } = useCurrentDocument()

  return (
    <StepSessionProvider
      step="patient_matching"
      documentKey={document.key}
    >
      <DocumentPatientMatchingContent />
    </StepSessionProvider>
  )
}

function DocumentPatientMatchingContent() {
  useDocumentProcessingPolling()
  const formRef = useRef<HTMLDivElement>(null)
  const { trackStepCompletion } = useStepCompletion()

  const [optionalNotes, setOptionalNotes] = useState('')
  const [hoveredField, setHoveredField] = useState<HoveredField>(null)
  const [showAllContradictions, setShowAllContradictions] = useState(false)
  const isPeerToPeerEnabled = useIsFeatureEnabled('2026-05-peer-to-peer')
  const isBilledAmountEnabled = useIsFeatureEnabled('2026-05-billed-amount-patient-matching')
  const isCarrierClaimIdEnabled = useIsFeatureEnabled('2026-06-carrier-claim-id')
  const stepFlag = useStepFlagState(FLAG_TYPES.STEP_PATIENT)
  const isStepFlaggingEnabled = useIsFeatureEnabled('2026-05-step-flagging')
  const isPaCaseMatchesEnabled = useIsFeatureEnabled('2026-07-pa-case-matches')
  const isPaMatchingCutoverEnabled = useIsFeatureEnabled('2026-08-pa-matching-cutover')
  const isPaMatchingDocDataEnabled = useIsFeatureEnabled('2026-08-pa-matching-doc-data')
  const isPaCardRedesignEnabled = useIsFeatureEnabled('2026-09-pa-card-redesign')
  const isAutoReviewedPaSelectionEnabled = useIsFeatureEnabled('2026-09-auto-reviewed-pa-selection')
  const isClaimCardRedesignEnabled = useIsFeatureEnabled('2026-10-claim-card-redesign')
  const isPaMatchingV2 = useIsFeatureEnabled('2026-10-pa-matching-v2')
  const isClaimMatchingV2 = useIsFeatureEnabled('2026-10-claim-matching-v2')
  const isMatchingV2 = isPaMatchingV2 || isClaimMatchingV2
  const {
    data: { document },
  } = useCurrentDocument()

  const { data } = useDocumentExtractedDenialData({ documentKey: document.key })
  const { submitPatientMatchingNotes, isSuccess: isSubmitPatientMatchingNotesSuccess } =
    useSubmitPatientMatchingNotes({ documentKey: document.key })
  const { isAuditTrailVisible } = useAuditTrail()
  const {
    data: { events: auditTrailEvents },
  } = useAuditTrailEvents({ documentKey: document.key })
  const optionalPatientMatchingComments = auditTrailEvents
    .filter(
      (event) =>
        (event.event_type === 'patientrev' || event.event_type === 'pa-patientrev') &&
        event.status !== 'validated' &&
        !!event.notes,
    )
    .reverse()

  const {
    data: {
      patient_data: legacyPatientData,
      selected_match_index: selectedMatchIndex,
      user_suggested_lims_case_file_id: userSuggestedLimsCaseFileId,
      user_suggested_patient_account_number: userSuggestedPatientAccountNumber,
      feedback_status: feedbackStatus,
      query: legacyMatchQuery,
    },
  } = usePatientMatches({ documentKey: document.key })
  const [extractedDataView, setExtractedDataView] = useState<'extracted' | 'pdf'>('extracted')
  const [isExactMatchDialogOpen, setIsExactMatchDialogOpen] = useState(false)
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const selectedPayor = payors.find((p) => p.payorId === document.payor_id)

  const showPaMatches =
    isPaCaseMatchesEnabled && PRIOR_AUTH_PHASES.includes(document.category_phase ?? '')
  const isPaMatchingCutover =
    isPaMatchingCutoverEnabled && PRIOR_AUTH_PHASES.includes(document.category_phase ?? '')
  const isPaDocWithNewPanel = isPaMatchingDocDataEnabled && showPaMatches
  const isClaimDoc =
    isClaimCardRedesignEnabled && CLAIM_PHASES.includes(document.category_phase ?? '')
  const isClaimDocWithNewPanel = isClaimDoc
  const { data: paMatchesData } = usePatientPaMatches({
    documentKey: document.key,
    enabled: showPaMatches,
  })

  const fieldsMap = useMemo(() => {
    return (data?.fields ?? []).reduce<Record<DataExtractionFieldName, string | undefined>>(
      (acc, field) => {
        acc[field.name as DataExtractionFieldName] = field.value as string
        return acc
      },
      {} as Record<DataExtractionFieldName, string | undefined>,
    )
  }, [data])

  const resolvedTestNames = useMemo(() => {
    if (isClaimDocWithNewPanel && legacyMatchQuery?.test_names?.length) {
      return legacyMatchQuery.test_names.join(', ')
    }
    if (
      isPaDocWithNewPanel &&
      isPaCardRedesignEnabled &&
      paMatchesData?.query?.test_names?.length
    ) {
      return paMatchesData.query.test_names.join(', ')
    }
    return null
  }, [
    isClaimDocWithNewPanel,
    isPaDocWithNewPanel,
    isPaCardRedesignEnabled,
    legacyMatchQuery?.test_names,
    paMatchesData?.query?.test_names,
  ])

  const claimFieldsMap = useMemo(() => {
    if (!resolvedTestNames) return fieldsMap
    return {
      ...fieldsMap,
      test_name: resolvedTestNames,
    }
  }, [fieldsMap, resolvedTestNames])

  const { assigningInProgress } = useAssignReviewer(trackQueueSelected)

  const {
    submitPatientMatchingReview,
    isPatientMatchingReviewError,
    isPatientMatchingReviewPending,
  } = useSubmitPatientMatchingReview({
    documentKey: document.key,
  })
  const { submitPaMatchingReview, isPaMatchingReviewError } = useSubmitPaMatchingReview({
    documentKey: document.key,
  })
  const { createPatientMatchAndValidate } = useCreatePatientMatchAndValidate({
    documentKey: document.key,
  })
  const { createPaPatientMatchAndValidate } = useCreatePaPatientMatchAndValidate({
    documentKey: document.key,
  })

  // Post-server-ack: fire PDF tab close reminder when the patient step is the
  // last real step of the flow. Patient-matching is never reached by EOB docs,
  // so EOB flags are always false. `isTaggingEnabled` is derived the same way
  // as in `patients.tsx` (category phase via subcategories). Fire-and-forget
  // so orchestrator rejections never bubble into UX.
  const subcategories = useSubcategories()
  const isTaggingEnabled = isTaggingClassification(document.category, subcategories)
  const shouldRemindAfterPatientValidation = isLastStep('patient', {
    isTaggingEnabled,
    isEob: false,
    isNonFinancialEob: false,
  })

  const autoAcceptPatientMatchIndex = useMemo(
    () =>
      isPaMatchingCutover
        ? null
        : getAutoAcceptPatientMatchIndex({ selectedMatchIndex, feedbackStatus }),
    [feedbackStatus, isPaMatchingCutover, selectedMatchIndex],
  )

  useEffect(() => {
    if (
      autoAcceptPatientMatchIndex !== null &&
      document.review_status !== DOCUMENT_SERVER_STATUS.processing
    ) {
      setIsExactMatchDialogOpen(true)
    }
  }, [autoAcceptPatientMatchIndex, document.review_status])

  const submitAutoAcceptPatientMatch = useCallback(async () => {
    await submitPatientMatchingReview({
      match_status: 'validated',
      selected_match_index: autoAcceptPatientMatchIndex,
    })
    trackStepCompletion('matched')
    if (shouldRemindAfterPatientValidation) {
      void closePdfTabOrRemind(document.key)
    }
  }, [
    autoAcceptPatientMatchIndex,
    document.key,
    shouldRemindAfterPatientValidation,
    submitPatientMatchingReview,
    trackStepCompletion,
  ])

  // When PA cutover is active but the PA endpoint has no feedback, fall back to
  // legacy data. This happens when a user-created match was validated through the
  // legacy path (createPatientMatchAndValidate).
  const legacyValidatedMatch = useMemo(() => {
    if (
      !isPaMatchingCutover ||
      paMatchesData?.feedback_status ||
      selectedMatchIndex == null ||
      feedbackStatus !== 'validated'
    ) {
      return null
    }
    const record = legacyPatientData?.[selectedMatchIndex]
    if (!record) return null
    return {
      ...record,
      matchIndex: -1,
      newlyAdded: true,
    } as PatientMatch
  }, [
    isPaMatchingCutover,
    paMatchesData?.feedback_status,
    selectedMatchIndex,
    feedbackStatus,
    legacyPatientData,
  ])

  const claimUserCreatedMatch = useMemo(() => {
    if (
      !isClaimDoc ||
      !isClaimMatchingV2 ||
      selectedMatchIndex == null ||
      feedbackStatus !== 'validated'
    ) {
      return null
    }
    const record = legacyPatientData?.[selectedMatchIndex]
    if (
      !record ||
      record.confidence_scores?.confidence_score ||
      record.matched_on?.length ||
      record.contradicted_on?.length
    ) {
      return null
    }
    return {
      ...record,
      matchIndex: -1,
      newlyAdded: true,
    } as PatientMatch
  }, [isClaimDoc, isClaimMatchingV2, selectedMatchIndex, feedbackStatus, legacyPatientData])

  const isAutoReviewed = document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed

  const defaultValues = useMemo(() => {
    const paFeedback = paMatchesData?.feedback_status as apiClient.MatchStatus | null | undefined
    return {
      match: {
        matchStatus: isPaMatchingCutover
          ? isAutoReviewedPaSelectionEnabled
            ? (paFeedback ??
              (legacyValidatedMatch ? 'validated' : null) ??
              (paMatchesData?.selected_match_index != null ? 'validated' : null) ??
              (isAutoReviewed && paMatchesData?.system_match_method != null ? 'no_match' : null))
            : (paFeedback ?? (legacyValidatedMatch ? 'validated' : null))
          : selectedMatchIndex !== null && selectedMatchIndex !== undefined
            ? 'validated'
            : (feedbackStatus ?? null),
        selectedMatchIndex: isPaMatchingCutover
          ? paFeedback === 'validated'
            ? (paMatchesData?.selected_match_index ?? undefined)
            : legacyValidatedMatch
              ? -1
              : isAutoReviewedPaSelectionEnabled
                ? (paMatchesData?.selected_match_index ?? undefined)
                : undefined
          : claimUserCreatedMatch
            ? -1
            : selectedMatchIndex !== undefined && selectedMatchIndex !== null
              ? selectedMatchIndex
              : autoAcceptPatientMatchIndex !== null
                ? autoAcceptPatientMatchIndex
                : undefined,
        limsCaseId: userSuggestedLimsCaseFileId ?? '',
        visitId: userSuggestedPatientAccountNumber ?? '',
      } as {
        matchStatus: apiClient.MatchStatus | null
        selectedMatchIndex: number | undefined
        limsCaseId: string
        visitId: string
      },
      hiddenMatchIndexes: [] as number[],
      notes:
        isPaMatchingCutover && paMatchesData?.feedback_status === 'no_match'
          ? (paMatchesData?.feedback_notes ?? '')
          : '',
      newlyAddedMatch: legacyValidatedMatch ?? claimUserCreatedMatch,
    }
  }, [
    feedbackStatus,
    isAutoReviewed,
    isAutoReviewedPaSelectionEnabled,
    isPaMatchingCutover,
    legacyValidatedMatch,
    claimUserCreatedMatch,
    paMatchesData?.feedback_status,
    paMatchesData?.selected_match_index,
    paMatchesData?.system_match_method,
    paMatchesData?.feedback_notes,
    selectedMatchIndex,
    autoAcceptPatientMatchIndex,
    userSuggestedLimsCaseFileId,
    userSuggestedPatientAccountNumber,
  ])

  const form = useAppForm({
    defaultValues,
    validators: {
      onChange: ({ value }) => {
        if (
          value.match.selectedMatchIndex === undefined &&
          value.match.matchStatus !== 'no_match'
        ) {
          return {
            fields: {
              match: { base: 'Match is required' } satisfies NoMatchError,
            },
          }
        }
        if (
          value.match.matchStatus === 'no_match' &&
          !value.notes.trim() &&
          !value.match.limsCaseId.trim() &&
          !value.match.visitId.trim()
        ) {
          return {
            fields: {
              notes: userSuggestedPatientAccountNumber
                ? 'No Visit ID? Add a short note here to explain your choice'
                : 'No Case Number? Add a short note here to explain your choice',
              match: {
                visitId: userSuggestedPatientAccountNumber
                  ? 'Enter the Visit ID if you have it'
                  : undefined,
                limsCaseId: userSuggestedLimsCaseFileId
                  ? 'Enter the Case Number if you have it'
                  : undefined,
              } satisfies NoMatchError,
            },
          }
        }
        return undefined
      },
    },
    onSubmit: async ({ value }) => {
      if (
        value.newlyAddedMatch &&
        value.newlyAddedMatch.matchIndex === value.match.selectedMatchIndex
      ) {
        if (isPaMatchingCutover && isPaCardRedesignEnabled) {
          await createPaPatientMatchAndValidate(value.newlyAddedMatch)
        } else {
          await createPatientMatchAndValidate(value.newlyAddedMatch)
        }
        trackStepCompletion('matched')
      } else if (isPaMatchingCutover) {
        await submitPaMatchingReview({
          notes: value.notes.trim() || undefined,
          ...(value.match.matchStatus === 'validated'
            ? {
                match_status: 'validated',
                selected_match_index: value.match.selectedMatchIndex,
              }
            : {
                match_status: 'no_match',
              }),
        })
        trackStepCompletion(value.match.matchStatus === 'validated' ? 'matched' : 'no_match')
      } else {
        await submitPatientMatchingReview({
          notes: value.notes.trim(),
          ...(value.match.matchStatus === 'validated'
            ? {
                match_status: 'validated',
                selected_match_index: value.match.selectedMatchIndex,
              }
            : {
                match_status: 'no_match',
                ...(value.match.limsCaseId
                  ? { user_suggested_lims_case_file_id: value.match.limsCaseId }
                  : {}),
                ...(value.match.visitId
                  ? { user_suggested_patient_account_number: value.match.visitId }
                  : {}),
              }),
        })
        trackStepCompletion(value.match.matchStatus === 'validated' ? 'matched' : 'no_match')
      }
      // Post-server-ack (both branches awaited above): fire-and-forget reminder
      // only when patient is the last step of the flow. Errors from either
      // mutation propagate out before this, so the reminder never fires on
      // failure paths.
      if (shouldRemindAfterPatientValidation) {
        void closePdfTabOrRemind(document.key)
      }
    },
  })

  const {
    state: historyValues,
    set: setHistoryValues,
    reset: resetHistoryValues,
    undo: undoHistory,
    redo: redoHistory,
    canUndo,
    canRedo,
  } = useUndoRedo({
    initialValues: defaultValues,
    setFieldValue: form.setFieldValue,
  })

  const hasMatchChanges = useStore(form.store, (state) => {
    const v = state.values
    return (
      v.match.selectedMatchIndex !== defaultValues.match.selectedMatchIndex ||
      v.match.matchStatus !== defaultValues.match.matchStatus ||
      v.newlyAddedMatch?.matchIndex !== defaultValues.newlyAddedMatch?.matchIndex
    )
  })

  const validate = useCallback(() => {
    setTimeout(() => {
      form.validate('change')
    }, 0)
  }, [form])

  const undo = useCallback(() => {
    undoHistory()
    validate()
  }, [undoHistory, validate])

  const redo = useCallback(() => {
    redoHistory()
    validate()
  }, [redoHistory, validate])

  const revert = useCallback(() => {
    form.reset()
    resetHistoryValues()
    validate()
  }, [form, resetHistoryValues, validate])

  const hasPaSelectionRestored = useRef(false)

  useEffect(() => {
    if (
      isPaMatchingCutover &&
      (paMatchesData?.feedback_status ||
        (isAutoReviewedPaSelectionEnabled && paMatchesData?.selected_match_index != null) ||
        (isAutoReviewedPaSelectionEnabled &&
          isAutoReviewed &&
          paMatchesData?.system_match_method != null)) &&
      !hasPaSelectionRestored.current
    ) {
      hasPaSelectionRestored.current = true
      form.reset()
      resetHistoryValues(defaultValues)
      validate()
    }
  }, [
    isAutoReviewed,
    isAutoReviewedPaSelectionEnabled,
    isPaMatchingCutover,
    paMatchesData?.feedback_status,
    paMatchesData?.selected_match_index,
    paMatchesData?.system_match_method,
    form,
    resetHistoryValues,
    defaultValues,
    validate,
  ])

  useEffect(() => {
    validate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <FirstInteractionTracker containerRef={formRef} />
      {document.review_status === DOCUMENT_SERVER_STATUS.processing && (
        <ProcessingAlert className="mx-5 mt-4" />
      )}
      <div className="relative h-full w-full">
        <div
          ref={formRef}
          className="flex h-full w-full min-w-[1280px] overflow-hidden"
        >
          {isExactMatchDialogOpen && (
            <Dialog
              className="[&_.mdc-dialog\_\_container]:relative [&_.mdc-dialog\_\_container]:block [&_.mdc-dialog\_\_container]:w-full [&_.mdc-dialog\_\_container]:max-w-[1440px] [&_.mdc-dialog\_\_scrim]:bg-transparent [&_.mdc-dialog\_\_surface]:absolute [&_.mdc-dialog\_\_surface]:top-[168px] [&_.mdc-dialog\_\_surface]:right-6 [&_.mdc-dialog\_\_surface]:shadow-[0_0.25px_3px_0_rgba(0,0,0,0.04),0_2.75px_9px_0_rgba(0,0,0,0.19)]"
              size="sm"
              onClose={() => setIsExactMatchDialogOpen(false)}
              dismissible={false}
              actions={
                <>
                  <Button
                    onClick={() => setIsExactMatchDialogOpen(false)}
                    disabled={isPatientMatchingReviewPending}
                  >
                    Review first
                  </Button>
                  <Button
                    raised
                    onClick={submitAutoAcceptPatientMatch}
                    disabled={isPatientMatchingReviewPending}
                  >
                    Accept & Finish
                  </Button>
                </>
              }
            >
              <OptiWalkingWithCheckmark />
              <div className="mt-4 flex flex-col gap-2">
                <Text
                  className="text-text-primary"
                  appearance="SUBTITLE2"
                >
                  Claim ID match found!
                </Text>
                <Text
                  className="text-text-secondary"
                  appearance="CAPTION"
                >
                  opti has 100% confidence in the selected match. You can accept now or review
                  details first.
                </Text>
              </div>
            </Dialog>
          )}
          <div className="border-divider bg-surface-low flex w-[360px] shrink-0 flex-col overflow-hidden border-r">
            <div className="flex shrink-0 flex-col gap-[22px] px-5 pt-4 pb-3">
              <div className="relative flex items-center gap-2">
                <Text
                  className="pt-px pb-0.5"
                  appearance="H6"
                >
                  Document Data
                </Text>
                <InfoTip
                  iconSize="24"
                  align="end"
                >
                  <div className="flex max-w-[245px] flex-col gap-2">
                    <OptiWalkingWithCheckmark width="50" />
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      I extracted these fields from the document to help you find the right patient.
                    </Text>
                  </div>
                </InfoTip>
                {isStepFlaggingEnabled && (
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <StepFlagButton
                      flagType={FLAG_TYPES.STEP_PATIENT}
                      isCurrentStep={stepFlag.isCurrentStep || (isMatchingV2 && hasMatchChanges)}
                    />
                  </div>
                )}
              </div>

              <SegmentedButton size="large">
                <Segment
                  checked={extractedDataView === 'extracted'}
                  onClick={() => setExtractedDataView('extracted')}
                >
                  Extracted
                </Segment>
                <Segment
                  checked={extractedDataView === 'pdf'}
                  onClick={() => setExtractedDataView('pdf')}
                >
                  <Icon children="picture_as_pdf" />
                </Segment>
              </SegmentedButton>
            </div>

            {extractedDataView === 'extracted' ? (
              <ScrollArea className="flex-1">
                <div className="flex w-[360px] flex-col">
                  <div
                    className={cn('border-divider border-b px-5 pb-3 whitespace-nowrap', {
                      'pt-4':
                        isClaimDocWithNewPanel || (isPaDocWithNewPanel && isPaCardRedesignEnabled),
                      'pt-1':
                        !isClaimDocWithNewPanel &&
                        !(isPaDocWithNewPanel && isPaCardRedesignEnabled),
                    })}
                  >
                    <Text appearance="OVERLINE">
                      {isClaimDocWithNewPanel || (isPaDocWithNewPanel && isPaCardRedesignEnabled)
                        ? 'Document data (derived or translated)'
                        : 'Payor document data'}
                    </Text>
                    <div className="mt-3 flex flex-col gap-2">
                      <Text
                        appearance="SUBTITLE"
                        placeholder={NO_DATA_PLACEHOLDER}
                        truncated
                        withTooltip
                        copyable
                      >
                        {(isClaimDocWithNewPanel ||
                          (isPaDocWithNewPanel && isPaCardRedesignEnabled)) &&
                        fieldsMap.patient_name
                          ? fieldsMap.patient_name
                              .toLowerCase()
                              .replace(/\b\w/g, (c) => c.toUpperCase())
                          : fieldsMap.patient_name}
                      </Text>
                      <div
                        className={cn(
                          'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                          hoveredField === 'birth_date' &&
                            'border-l-primary-main bg-surface-medium',
                        )}
                        onMouseEnter={() => setHoveredField('birth_date')}
                        onMouseLeave={() => setHoveredField(null)}
                      >
                        <Text
                          className="text-text-secondary"
                          appearance="BODY2"
                        >
                          Birth Date
                        </Text>
                        <Text
                          appearance="BODY2"
                          placeholder={NO_DATA_PLACEHOLDER}
                          truncated
                          withTooltip
                          copyable
                        >
                          {fieldsMap.patient_dob ? formatDateDefault(fieldsMap.patient_dob) : null}
                        </Text>
                      </div>
                      <div
                        className={cn(
                          'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                          hoveredField === 'insurance_company' &&
                            'border-l-primary-main bg-surface-medium',
                        )}
                        onMouseEnter={() => setHoveredField('insurance_company')}
                        onMouseLeave={() => setHoveredField(null)}
                      >
                        <Text
                          className="text-text-secondary"
                          appearance="BODY2"
                        >
                          Payor Name
                        </Text>
                        <Text
                          appearance="BODY2"
                          placeholder={NO_DATA_PLACEHOLDER}
                          truncated
                          withTooltip
                          copyable
                        >
                          {(isPaDocWithNewPanel && isPaCardRedesignEnabled) ||
                          isClaimDocWithNewPanel
                            ? fieldsMap.insurance_company
                            : (selectedPayor?.payorName ?? fieldsMap.insurance_company)}
                        </Text>
                      </div>
                      <div
                        className={cn(
                          'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                          hoveredField === 'test_name' && 'border-l-primary-main bg-surface-medium',
                        )}
                        onMouseEnter={() => setHoveredField('test_name')}
                        onMouseLeave={() => setHoveredField(null)}
                      >
                        <div className="flex shrink-0 items-center gap-1">
                          <Text
                            className="text-text-secondary"
                            appearance="BODY2"
                          >
                            Test Name
                          </Text>
                          <InfoTip iconSize="16">
                            <Text
                              className="text-text-secondary max-w-[265px] pr-6"
                              appearance="CAPTION"
                            >
                              The Natera test product name associated with this case.
                            </Text>
                          </InfoTip>
                        </div>
                        <Text
                          appearance="BODY2"
                          placeholder={NO_DATA_PLACEHOLDER}
                          truncated
                          withTooltip
                          copyable
                        >
                          {(isPaDocWithNewPanel && isPaCardRedesignEnabled && isPaMatchingV2) ||
                          (isClaimDocWithNewPanel && isClaimMatchingV2)
                            ? resolvedTestNames
                            : fieldsMap.test_name}
                        </Text>
                      </div>
                      {isPeerToPeerEnabled &&
                        !isClaimDocWithNewPanel &&
                        !(isPaDocWithNewPanel && isPaCardRedesignEnabled) && (
                          <div
                            className={cn(
                              'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                              hoveredField === 'cpt_code' &&
                                'border-l-primary-main bg-surface-medium',
                            )}
                            onMouseEnter={() => setHoveredField('cpt_code')}
                            onMouseLeave={() => setHoveredField(null)}
                          >
                            <Text
                              className="text-text-secondary"
                              appearance="BODY2"
                            >
                              CPT Code
                            </Text>
                            <Text
                              appearance="BODY2"
                              placeholder={NO_DATA_PLACEHOLDER}
                              truncated
                              withTooltip
                              copyable
                            >
                              {fieldsMap.cpt_code}
                            </Text>
                          </div>
                        )}
                      <div
                        className={cn(
                          'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                          hoveredField === 'date_of_service' &&
                            'border-l-primary-main bg-surface-medium',
                        )}
                        onMouseEnter={() => setHoveredField('date_of_service')}
                        onMouseLeave={() => setHoveredField(null)}
                      >
                        <Text
                          className="text-text-secondary"
                          appearance="BODY2"
                        >
                          Date of Service
                        </Text>
                        <Text
                          appearance="BODY2"
                          placeholder={NO_DATA_PLACEHOLDER}
                          truncated
                          withTooltip
                          copyable
                        >
                          {fieldsMap.service_date_from
                            ? formatDateDefault(fieldsMap.service_date_from)
                            : null}
                        </Text>
                      </div>
                      {(isClaimDocWithNewPanel ||
                        (isBilledAmountEnabled && !isPaDocWithNewPanel)) && (
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'billprice' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('billprice')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <Text
                            className="text-text-secondary"
                            appearance="BODY2"
                          >
                            {isClaimDocWithNewPanel ? 'Total Billed' : 'Billed Amount'}
                          </Text>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {formatCurrency(fieldsMap.total_amount)}
                          </Text>
                        </div>
                      )}
                      {isClaimDocWithNewPanel && (
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'appeal_date' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('appeal_date')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <Text
                            className="text-text-secondary"
                            appearance="BODY2"
                          >
                            Appeal Date
                          </Text>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {fieldsMap.appeal_date_received
                              ? formatDateDefault(fieldsMap.appeal_date_received)
                              : null}
                          </Text>
                        </div>
                      )}
                    </div>

                    <div
                      className={cn('border-divider border-b', {
                        'my-4':
                          isClaimDocWithNewPanel ||
                          (isPaDocWithNewPanel && isPaCardRedesignEnabled),
                        'my-5':
                          !isClaimDocWithNewPanel &&
                          !(isPaDocWithNewPanel && isPaCardRedesignEnabled),
                      })}
                    />

                    {isClaimDocWithNewPanel ? (
                      <div className="flex flex-col gap-3">
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'member_id' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('member_id')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex items-center gap-1">
                            <Text
                              className="text-text-secondary"
                              appearance="BODY2"
                            >
                              Member ID
                            </Text>
                            <InfoTip iconSize="16">
                              <Text
                                className="text-text-secondary max-w-[265px] pr-6"
                                appearance="CAPTION"
                              >
                                AKA: Subscriber ID, Subscriber Number, Member Number, Policy Number.
                                <br />A unique identifier for the patient's insurance policy
                                provided by the payor. This can be numbers only or an alphanumeric
                                combination.
                              </Text>
                            </InfoTip>
                          </div>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {fieldsMap.member_id}
                          </Text>
                        </div>
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'case_number' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('case_number')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex items-center gap-1">
                            <Text
                              className="text-text-secondary"
                              appearance="BODY2"
                            >
                              Case Number
                            </Text>
                            <InfoTip iconSize="16">
                              <Text
                                className="text-text-secondary max-w-[265px] pr-6"
                                appearance="CAPTION"
                              >
                                AKA: Case ID, LIMS Case ID.
                                <br />A unique identifier assigned by Natera Sales Order System
                                (SOS) to a test that ties back to the physical sample we receive
                                from a patient.
                              </Text>
                            </InfoTip>
                          </div>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {fieldsMap.case_number}
                          </Text>
                        </div>
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'patient_account_number' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('patient_account_number')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex items-center gap-1">
                            <Text
                              className="text-text-secondary"
                              appearance="BODY2"
                            >
                              Visit ID
                            </Text>
                            <InfoTip iconSize="16">
                              <Text
                                className="text-text-secondary max-w-[265px] pr-6"
                                appearance="CAPTION"
                              >
                                AKA: Patient Account Number, Patient ID, AMD Claim ID + submission
                                order.
                                <br />A unique identifier that represents the claim and how many
                                times we've submitted it created by Natera in AMD.
                              </Text>
                            </InfoTip>
                          </div>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {fieldsMap.patient_account_number?.replace(/[A-Za-z]+$/, '')}
                          </Text>
                        </div>
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'claim_number' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('claim_number')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex items-center gap-1">
                            <Text
                              className="text-text-secondary"
                              appearance="BODY2"
                            >
                              Payor Claim ID
                            </Text>
                            <InfoTip iconSize="16">
                              <Text
                                className="text-text-secondary max-w-[265px] pr-6"
                                appearance="CAPTION"
                              >
                                The claim number assigned by the payor for this claim submission.
                              </Text>
                            </InfoTip>
                          </div>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {fieldsMap.natera_claim_id ?? fieldsMap.claim_number}
                          </Text>
                        </div>
                        <div
                          className={cn(
                            'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                            hoveredField === 'cpt_code' &&
                              'border-l-primary-main bg-surface-medium',
                          )}
                          onMouseEnter={() => setHoveredField('cpt_code')}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex items-center gap-1">
                            <Text
                              className="text-text-secondary"
                              appearance="BODY2"
                            >
                              CPT Code(s)
                            </Text>
                            <InfoTip iconSize="16">
                              <Text
                                className="text-text-secondary max-w-[265px] pr-6"
                                appearance="CAPTION"
                              >
                                CPT (Current Procedural Terminology) codes are standardized,
                                five-digit numeric codes used by healthcare providers to document
                                medical procedures and services.
                              </Text>
                            </InfoTip>
                          </div>
                          <Text
                            appearance="BODY2"
                            placeholder={NO_DATA_PLACEHOLDER}
                            truncated
                            withTooltip
                            copyable
                          >
                            {fieldsMap.cpt_code}
                          </Text>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn('flex flex-col', {
                          'gap-3': isPaDocWithNewPanel && isPaCardRedesignEnabled,
                          'gap-2': !(isPaDocWithNewPanel && isPaCardRedesignEnabled),
                        })}
                      >
                        {!isPaDocWithNewPanel && (
                          <div
                            className={cn(
                              'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                              hoveredField ===
                                (isCarrierClaimIdEnabled ? 'natera_claim_id' : 'claim_number') &&
                                'border-l-primary-main bg-surface-medium',
                            )}
                            onMouseEnter={() => {
                              if (
                                isCarrierClaimIdEnabled
                                  ? fieldsMap.natera_claim_id
                                  : fieldsMap.claim_number
                              ) {
                                setHoveredField(
                                  isCarrierClaimIdEnabled ? 'natera_claim_id' : 'claim_number',
                                )
                              }
                            }}
                            onMouseLeave={() => setHoveredField(null)}
                          >
                            <div className="flex items-center gap-1">
                              <Text
                                className="text-text-secondary"
                                appearance="BODY2"
                              >
                                {isCarrierClaimIdEnabled ? 'Natera Claim ID' : 'Claim ID'}
                              </Text>
                              <InfoTip iconSize="16">
                                <Text
                                  className="text-text-secondary max-w-[265px] pr-6"
                                  appearance="CAPTION"
                                >
                                  AKA: Claim Number.
                                  <br />
                                  The number assigned to a claim by the payor.
                                </Text>
                              </InfoTip>
                            </div>
                            <Text
                              appearance="BODY2"
                              placeholder={NO_DATA_PLACEHOLDER}
                              truncated
                              withTooltip
                              copyable
                            >
                              {isCarrierClaimIdEnabled
                                ? fieldsMap.natera_claim_id
                                : fieldsMap.claim_number}
                            </Text>
                          </div>
                        )}
                        {!isPaDocWithNewPanel && isCarrierClaimIdEnabled && (
                          <div
                            className={cn(
                              'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                              hoveredField === 'carrier_claim_id' &&
                                'border-l-primary-main bg-surface-medium',
                            )}
                            onMouseEnter={() => setHoveredField('carrier_claim_id')}
                            onMouseLeave={() => setHoveredField(null)}
                          >
                            <div className="flex items-center gap-1">
                              <Text
                                className="text-text-secondary"
                                appearance="BODY2"
                              >
                                Carrier Claim ID
                              </Text>
                              <InfoTip iconSize="16">
                                <Text
                                  className="text-text-secondary max-w-[265px] pr-6"
                                  appearance="CAPTION"
                                >
                                  The payer-assigned claim number used for carrier matching.
                                </Text>
                              </InfoTip>
                            </div>
                            <Text
                              appearance="BODY2"
                              placeholder={NO_DATA_PLACEHOLDER}
                              truncated
                              withTooltip
                              copyable
                            >
                              {fieldsMap.claim_number}
                            </Text>
                          </div>
                        )}
                        {!isPaDocWithNewPanel && (
                          <div
                            className={cn(
                              'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                              hoveredField === 'patient_account_number' &&
                                'border-l-primary-main bg-surface-medium',
                            )}
                            onMouseEnter={() => setHoveredField('patient_account_number')}
                            onMouseLeave={() => setHoveredField(null)}
                          >
                            <div className="flex items-center gap-1">
                              <Text
                                className="text-text-secondary"
                                appearance="BODY2"
                              >
                                Visit ID
                              </Text>
                              <InfoTip iconSize="16">
                                <Text
                                  className="text-text-secondary max-w-[265px] pr-6"
                                  appearance="CAPTION"
                                >
                                  AKA: Patient Account Number, Patient ID, AMD Claim ID + submission
                                  order.
                                  <br />
                                  A unique identifier that represents the claim and how many times
                                  we've submitted it created by Natera in AMD. The format is the AMD
                                  Visit ID + an alpha character that represents the submission order
                                  / instance; A = 1st submission. ie: 12345678A
                                  <br />
                                  Patient number will never be on prior authorization docs unless
                                  talking about claim & appeal related PA docs. PA submission comes
                                  before AMD visit creation & claim submission.
                                </Text>
                              </InfoTip>
                            </div>
                            <Text
                              appearance="BODY2"
                              placeholder={NO_DATA_PLACEHOLDER}
                              truncated
                              withTooltip
                              copyable
                            >
                              {fieldsMap.patient_account_number}
                            </Text>
                          </div>
                        )}
                        {isPaDocWithNewPanel && isPaCardRedesignEnabled ? (
                          <>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'member_id' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('member_id')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  Member ID
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    AKA: Subscriber ID, Subscriber Number, Member Number, Policy
                                    Number.
                                    <br />A unique identifier for the patient's insurance policy
                                    provided by the payor. This can be numbers only or an
                                    alphanumeric combination.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.member_id}
                              </Text>
                            </div>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'case_number' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('case_number')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  Case Number
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    This is the case number or ID assigned by a Payor to a claim or
                                    prior auth submission.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.case_number}
                              </Text>
                            </div>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'reference_number' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('reference_number')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  Reference Number
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    A reference or tracking number from the payor associated with
                                    this prior authorization.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.reference_number}
                              </Text>
                            </div>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'authorization_code' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('authorization_code')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  Authorization Code
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    Authorization number or reference associated with prior
                                    authorizations, approvals, denials, or appeals.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.authorization_code}
                              </Text>
                            </div>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'cpt_code' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('cpt_code')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  CPT Code(s)
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    CPT (Current Procedural Terminology) codes are standardized,
                                    five-digit numeric codes used by healthcare providers to
                                    document medical procedures and services.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.cpt_code}
                              </Text>
                            </div>
                          </>
                        ) : (
                          <>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'case_number' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('case_number')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  Case Number
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    AKA: Case ID, LIMS Case ID.
                                    <br />A unique identifier assigned by Natera Sales Order System
                                    (SOS) to a test that ties back to the physical sample we receive
                                    from a patient.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.case_number}
                              </Text>
                            </div>
                            <div
                              className={cn(
                                'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                hoveredField === 'member_id' &&
                                  'border-l-primary-main bg-surface-medium',
                              )}
                              onMouseEnter={() => setHoveredField('member_id')}
                              onMouseLeave={() => setHoveredField(null)}
                            >
                              <div className="flex items-center gap-1">
                                <Text
                                  className="text-text-secondary"
                                  appearance="BODY2"
                                >
                                  Member ID
                                </Text>
                                <InfoTip iconSize="16">
                                  <Text
                                    className="text-text-secondary max-w-[265px] pr-6"
                                    appearance="CAPTION"
                                  >
                                    AKA: Subscriber ID, Subscriber Number, Member Number, Policy
                                    Number.
                                    <br />A unique identifier for the patient's insurance policy
                                    provided by the payor. This can be numbers only or an
                                    alphanumeric combination.
                                  </Text>
                                </InfoTip>
                              </div>
                              <Text
                                appearance="BODY2"
                                placeholder={NO_DATA_PLACEHOLDER}
                                truncated
                                withTooltip
                                copyable
                              >
                                {fieldsMap.member_id}
                              </Text>
                            </div>
                            {isPaDocWithNewPanel && (
                              <div
                                className={cn(
                                  'flex items-center justify-between gap-4 border-l-2 border-l-transparent transition-colors',
                                  hoveredField === 'reference_number' &&
                                    'border-l-primary-main bg-surface-medium',
                                )}
                                onMouseEnter={() => setHoveredField('reference_number')}
                                onMouseLeave={() => setHoveredField(null)}
                              >
                                <Text
                                  className="text-text-secondary shrink-0"
                                  appearance="BODY2"
                                >
                                  Reference Number
                                </Text>
                                <Text
                                  appearance="BODY2"
                                  placeholder={NO_DATA_PLACEHOLDER}
                                  truncated
                                  withTooltip
                                  copyable
                                >
                                  {fieldsMap.reference_number}
                                </Text>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {(isClaimDocWithNewPanel || (isPaDocWithNewPanel && isPaCardRedesignEnabled)) && (
                    <div className="px-5 pt-4 pb-4">
                      <label className="flex cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          className="accent-primary-main h-4 w-4 cursor-pointer"
                          checked={showAllContradictions}
                          onChange={(e) => setShowAllContradictions(e.target.checked)}
                        />
                        <Text appearance="BODY2">Show all contradictions</Text>
                      </label>
                    </div>
                  )}

                  <div className="bg-surface-lowest flex h-full flex-col gap-3 p-5">
                    {optionalPatientMatchingComments.length === 0 ? (
                      <Text
                        className="py-[5px]"
                        appearance="OVERLINE"
                      >
                        Comments
                      </Text>
                    ) : (
                      <CollapsibleRoot defaultOpen={false}>
                        <CollapsibleTrigger
                          label="Comments"
                          counter={optionalPatientMatchingComments.length}
                        />
                        <CollapsibleContent>
                          <div className="mt-3 flex flex-col gap-3">
                            {optionalPatientMatchingComments.map((comment) => {
                              const date = comment.timestamp
                                ? new Date(comment.timestamp)
                                : new Date()
                              return (
                                <div
                                  key={comment.timestamp}
                                  className="flex flex-col py-1"
                                >
                                  <div className="flex gap-1">
                                    <div className="flex w-full items-center justify-between gap-1">
                                      <div className="flex items-start gap-1">
                                        <div className="text-text-secondary flex items-center gap-2 whitespace-nowrap">
                                          <Text
                                            className="uppercase"
                                            appearance="CAPTION"
                                          >
                                            {formatTimeDefault(comment.timestamp ?? 0)}
                                          </Text>
                                          <span className="bg-text-secondary h-1 w-1 rounded-full" />
                                          <Text
                                            className="uppercase"
                                            appearance="CAPTION"
                                            title={
                                              isToday(date) || isYesterday(date)
                                                ? formatDateDefault(date)
                                                : undefined
                                            }
                                          >
                                            {isToday(date)
                                              ? 'Today'
                                              : isYesterday(date)
                                                ? 'Yesterday'
                                                : formatDateDefault(date)}
                                          </Text>
                                          <LocalIcon
                                            name="personOutlined"
                                            size="16"
                                          />
                                        </div>
                                        <Text appearance="CAPTION">
                                          {comment.user?.first_name} {comment.user?.last_name}
                                        </Text>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="mt-2 flex flex-col gap-1">
                                    {comment.status === 'no_match' ? (
                                      <div className="flex items-start gap-2">
                                        <LocalIcon
                                          className="text-alert-info-message mt-[3px] shrink-0"
                                          name="attachNoteOutlined"
                                          size="16"
                                        />
                                        <Text
                                          className="text-alert-info-message"
                                          appearance="BODY2"
                                        >
                                          Reason for change
                                        </Text>
                                      </div>
                                    ) : null}
                                    <Text appearance="CAPTION">{comment.notes}</Text>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </CollapsibleContent>
                      </CollapsibleRoot>
                    )}

                    {isSubmitPatientMatchingNotesSuccess ? (
                      <div className="border-alert-info-message text-chip-status-info-content flex h-[85px] items-center justify-center rounded-lg border p-3 text-center">
                        <Text appearance="CAPTION">
                          Saved! This note will come in handy for future reviews.
                        </Text>
                      </div>
                    ) : (
                      <Textarea
                        placeholder="Leave your comment"
                        className="min-h-[85px]"
                        value={optionalNotes}
                        onChange={(e) => setOptionalNotes(e.target.value)}
                        onBlur={() => {
                          if (optionalNotes.trim().length >= 3) {
                            submitPatientMatchingNotes({
                              notes: optionalNotes.trim(),
                            })
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex-1 overflow-hidden">
                <DocumentPDF
                  className="h-full max-w-[360px]"
                  documentKey={document.key}
                  initialFitToWidth
                />
              </div>
            )}
          </div>

          {assigningInProgress ? (
            <LoadingSkeleton />
          ) : (
            <Suspense fallback={<LoadingSkeleton />}>
              <StepViewTracker />
              <Patients
                form={form}
                historyValues={historyValues}
                setHistoryValues={setHistoryValues}
                revert={revert}
                undo={undo}
                redo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                isPatientMatchingReviewError={
                  isPatientMatchingReviewError || isPaMatchingReviewError
                }
                hoveredField={hoveredField}
                documentFields={
                  isClaimDoc || (isPaDocWithNewPanel && isPaCardRedesignEnabled)
                    ? claimFieldsMap
                    : fieldsMap
                }
                autoAcceptPatientMatchIndex={autoAcceptPatientMatchIndex}
                paMatchesData={showPaMatches ? paMatchesData : undefined}
                isPaMatchingCutover={isPaMatchingCutover}
                isPaCardRedesignEnabled={isPaCardRedesignEnabled}
                showAllContradictions={showAllContradictions}
                isClaimCardRedesign={isClaimDoc}
                isPaMatchingV2={isPaMatchingV2}
                isClaimMatchingV2={isClaimMatchingV2}
                resolvedTestNames={isMatchingV2 ? resolvedTestNames : null}
              />
            </Suspense>
          )}
        </div>
        {isAuditTrailVisible && (
          <div className="bg-surface-lowest border-divider absolute top-0 right-0 z-10 h-full border-l">
            <Suspense fallback={<AuditTrailLoadingSkeleton />}>
              <AuditTrail
                page="patient"
                assetId={document.key}
              />
            </Suspense>
          </div>
        )}
      </div>
    </>
  )
}

function StepViewTracker() {
  useStepView()
  return null
}

function FirstInteractionTracker({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  useFirstInteraction(containerRef)
  return null
}
